import {
  CARD_GAP,
  CARD_MARGIN,
  MOBILE_MAX,
  TIP_GAP,
  TIP_MARGIN,
} from './constants';

// Placement maths for the tooltip and the tour card. Pure functions: they
// take the target's viewport rect, the box's size and the viewport size, and
// return where the box goes, which side it ended up on and where its arrow
// points.

export const clamp = (value, lo, hi) => Math.max(lo, Math.min(hi, value));

const TIP_ORDERS = {
  top: ['top', 'bottom', 'right', 'left'],
  bottom: ['bottom', 'top', 'right', 'left'],
  left: ['left', 'right', 'top', 'bottom'],
  right: ['right', 'left', 'top', 'bottom'],
};

// Tooltip: the preferred side if it fits, else the next in TIP_ORDERS, else
// top. The box stays `margin` inside the viewport and the arrow keeps
// pointing at the target's centre.
// → { left, top, side, arrow } — arrow is the x (top/bottom) or y
// (left/right) offset inside the box.
export const placeTip = ({
  rect,
  width,
  height,
  vw,
  vh,
  side = 'top',
  margin = TIP_MARGIN,
  gap = TIP_GAP,
}) => {
  const fits = {
    top: rect.top - gap - height >= margin,
    bottom: rect.bottom + gap + height <= vh - margin,
    left: rect.left - gap - width >= margin,
    right: rect.right + gap + width <= vw - margin,
  };
  const order = TIP_ORDERS[side] || TIP_ORDERS.top;
  const chosen = order.find((s) => fits[s]) || 'top';
  let x;
  let y;
  if (chosen === 'top' || chosen === 'bottom') {
    x = rect.left + rect.width / 2 - width / 2;
    y = chosen === 'top' ? rect.top - gap - height : rect.bottom + gap;
  } else {
    y = rect.top + rect.height / 2 - height / 2;
    x = chosen === 'left' ? rect.left - gap - width : rect.right + gap;
  }
  const left = Math.round(clamp(x, margin, vw - width - margin));
  const top = Math.round(clamp(y, margin, vh - height - margin));
  const arrow =
    chosen === 'top' || chosen === 'bottom'
      ? clamp(rect.left + rect.width / 2 - left, 12, width - 12)
      : clamp(rect.top + rect.height / 2 - top, 10, height - 10);
  return { left, top, side: chosen, arrow };
};

// The spotlight: the target's rect grown by `pad` on every side.
export const padRect = (rect, pad) => ({
  left: rect.left - pad,
  top: rect.top - pad,
  width: rect.width + pad * 2,
  height: rect.height + pad * 2,
});

// One rect around several targets (a step can light up a group).
export const unionRect = (rects) => {
  if (!rects.length) return null;
  const left = Math.min(...rects.map((r) => r.left));
  const top = Math.min(...rects.map((r) => r.top));
  const right = Math.max(...rects.map((r) => r.left + r.width));
  const bottom = Math.max(...rects.map((r) => r.top + r.height));
  return { left, top, width: right - left, height: bottom - top };
};

// The visible cut-out: the padded rect kept 4px inside the viewport.
export const holeRect = (padded, vw, vh) => {
  const left = Math.max(4, padded.left);
  const top = Math.max(4, padded.top);
  return {
    left,
    top,
    width: Math.min(padded.width, vw - 8 - left),
    height: Math.min(padded.height, vh - 8 - top),
  };
};

const CARD_ORDER = ['bottom', 'top', 'right', 'left'];

// Tour card: below the target by default, otherwise above, right or left
// (a step's preferred side first). On phones it pins to the bottom with no
// arrow; with no target it centres; when nothing fits it tucks into the
// target's bottom-right corner, still on screen.
// `spot` is the padded target rect (or null).
export const placeTourCard = ({
  spot,
  width,
  height,
  vw,
  vh,
  side = null,
  margin = CARD_MARGIN,
  gap = CARD_GAP,
  mobileMax = MOBILE_MAX,
}) => {
  if (vw <= mobileMax) {
    return { left: margin, top: vh - height - margin, side: 'none', arrow: 0 };
  }
  if (!spot) {
    return {
      left: vw / 2 - width / 2,
      top: vh / 2 - height / 2,
      side: 'none',
      arrow: 0,
    };
  }
  const right = spot.left + spot.width;
  const bottom = spot.top + spot.height;
  const fits = {
    bottom: bottom + gap + height <= vh - margin,
    top: spot.top - gap - height >= margin,
    right: right + gap + width <= vw - margin,
    left: spot.left - gap - width >= margin,
  };
  const order = side ? [side].concat(CARD_ORDER) : CARD_ORDER;
  const chosen = order.find((s) => fits[s]) || 'none';
  let left;
  let top;
  if (chosen === 'bottom' || chosen === 'top') {
    left = clamp(
      spot.left + spot.width / 2 - width / 2,
      margin,
      vw - width - margin
    );
    top = chosen === 'bottom' ? bottom + gap : spot.top - gap - height;
  } else if (chosen === 'right' || chosen === 'left') {
    top = clamp(
      spot.top + spot.height / 2 - height / 2,
      margin,
      vh - height - margin
    );
    left = chosen === 'right' ? right + gap : spot.left - gap - width;
  } else {
    left = clamp(right - width - 20, margin, vw - width - margin);
    top = clamp(bottom - height - 20, margin, vh - height - margin);
  }
  const arrow =
    chosen === 'top' || chosen === 'bottom'
      ? clamp(spot.left + spot.width / 2 - left, 22, width - 22)
      : clamp(spot.top + spot.height / 2 - top, 22, height - 22);
  return { left, top, side: chosen, arrow: chosen === 'none' ? 0 : arrow };
};
